import axios from '../axiosConfig'
import { getEnv } from '@utils/env'
import type { DashboardPeriod } from './finance.api'

const BUSINESS_LAYER_API = getEnv('REACT_APP_BUSINESS_LAYER_DASHBOARD')

interface ApiResponse<T> {
  statusCode: number
  message: string
  data: T
}

export interface TreatmentQueryParams {
  clinicId: string
  date: string
  period: DashboardPeriod
}

export interface IncompleteTreatmentOverviewRow {
  id: string
  patientId: string
  patientName: string
  lastVisit: string
  actions: string
}

export interface IncompleteTreatmentOverviewResult {
  period: DashboardPeriod
  periodStart: string
  periodEnd: string
  rows: IncompleteTreatmentOverviewRow[]
}

export interface IncompleteTreatmentKpiData {
  totalIncomplete: number
  totalIncompleteGoal: number | null
  totalPendingValue: string
  avgValuePerPatient: string
  overduePatients: number
  scheduledPatients: number
  conversionRate: string
  comparisonLabel: string
  totalDelta: string
  totalTrend: 'up' | 'down'
  pendingValueDelta: string
  pendingValueTrend: 'up' | 'down'
  conversionDelta: string
  conversionTrend: 'up' | 'down'
}

export interface IncompleteTxChartPoint {
  label: string
  restorative: number
  perio: number
  prosthodontics: number
  other: number
}

export interface IncompleteTxChartConfig {
  data: IncompleteTxChartPoint[]
  yAxisDomain: [number, number]
  yAxisTicks: number[]
}

export interface IncompleteTxPatientRow {
  id: string
  patientId: string
  patientName: string
  lastVisit: string
  category: string
  estimatedValue: string
  daysOverdue: number
  status: string
  provider: string
}

export interface IncompleteTxByCategoryRow {
  id: string
  category: string
  patients: number
  pendingValue: string
  avgValue: string
  pctOfTotal: string
  scheduled: number
  overdue: number
}

export interface IncompleteTxByProviderRow {
  id: string
  provider: string
  patients: number
  pendingValue: string
  avgValue: string
  scheduled: number
  overdue: number
  conversionRate: string
}

export interface IncompleteTreatmentDetailResult {
  period: DashboardPeriod
  periodStart: string
  periodEnd: string
  kpis: IncompleteTreatmentKpiData
  chart: IncompleteTxChartConfig
  patients: IncompleteTxPatientRow[]
  byCategory: IncompleteTxByCategoryRow[]
  byProvider: IncompleteTxByProviderRow[]
}

const treatmentDetailGet = <T>(
  path: string,
  params: TreatmentQueryParams,
  options?: { signal?: AbortSignal },
): Promise<T> =>
  axios
    .get<
      ApiResponse<T>
    >(`${BUSINESS_LAYER_API}/api/v1/dashboard/treatment/${path}`, { params, signal: options?.signal })
    .then((response) => response.data.data)

export const getIncompleteTreatmentOverview = (
  params: TreatmentQueryParams,
  options?: { signal?: AbortSignal },
): Promise<IncompleteTreatmentOverviewResult> =>
  treatmentDetailGet('incomplete/overview', params, options)

export const getIncompleteTreatmentDetail = (
  params: TreatmentQueryParams,
  options?: { signal?: AbortSignal },
): Promise<IncompleteTreatmentDetailResult> =>
  treatmentDetailGet('incomplete/detail', params, options)

export interface OpenTreatmentOverviewRow {
  id: string
  patientId: string
  patientName: string
  unschedAmount: string
  actions: string
}

export interface OpenTreatmentOverviewResult {
  period: DashboardPeriod
  periodStart: string
  periodEnd: string
  rows: OpenTreatmentOverviewRow[]
}

export interface OpenTreatmentKpiData {
  totalOpenPatients: number
  totalOpenPatientsGoal: number | null
  totalUnscheduledValue: string
  avgValuePerPatient: string
  highPriorityPatients: number
  patientsContacted: number | null
  schedulingRate: string | null
  comparisonLabel: string
  totalDelta: string
  totalTrend: 'up' | 'down'
  valueDelta: string
  valueTrend: 'up' | 'down'
  schedulingDelta: string | null
  schedulingTrend: 'up' | 'down'
}

export interface OpenTxChartPoint {
  label: string
  restorative: number
  perio: number
  prosthodontics: number
  other: number
}

export interface OpenTxChartConfig {
  data: OpenTxChartPoint[]
  yAxisDomain: [number, number]
  yAxisTicks: number[]
}

export interface OpenTxPatientRow {
  id: string
  patientId: string
  patientName: string
  unscheduledValue: string
  category: string
  lastVisit: string
  daysSinceContact: number | null
  priority: string
  provider: string
}

export interface OpenTxByCategoryRow {
  id: string
  category: string
  patients: number
  totalValue: string
  avgValue: string
  pctOfTotal: string
  highPriority: number
}

export interface OpenTxByProviderRow {
  id: string
  provider: string
  patients: number
  totalValue: string
  avgValue: string
  highPriority: number
  contacted: number | null
  schedulingRate: string | null
}

export interface OpenTreatmentDetailResult {
  period: DashboardPeriod
  periodStart: string
  periodEnd: string
  kpis: OpenTreatmentKpiData
  chart: OpenTxChartConfig
  patients: OpenTxPatientRow[]
  byCategory: OpenTxByCategoryRow[]
  byProvider: OpenTxByProviderRow[]
}

export const getOpenTreatmentOverview = (
  params: TreatmentQueryParams,
  options?: { signal?: AbortSignal },
): Promise<OpenTreatmentOverviewResult> =>
  treatmentDetailGet('open/overview', params, options)

export const getOpenTreatmentDetail = (
  params: TreatmentQueryParams,
  options?: { signal?: AbortSignal },
): Promise<OpenTreatmentDetailResult> =>
  treatmentDetailGet('open/detail', params, options)
